import { useQuery } from "@tanstack/react-query";
import { onboardingApi } from "@/lib/api";
import {
  ONBOARDING_STATUS_KEY,
  ONBOARDING_STATUS_STALE_MS,
} from "@/features/onboarding/onboardingStatusQuery";
import { useAuth } from "@/hooks/useAuth";
import { isSchoolAdminUser } from "@/lib/userRoutes";

/**
 * ADMIN-B2-ADOPT-1 — the Getting Started rail entry, present only while setup is incomplete.
 *
 * A checklist that stays in the rail after it is finished stops being a checklist and becomes
 * furniture; a checklist that vanishes with no other door becomes unreachable, which is the wound
 * this wave was called to close. So: rail entry while incomplete, and School Setup carries a quiet
 * permanent link once it is done. Both doors, neither redundant.
 *
 * FAIL-CLOSED ON UNKNOWN: while the request is in flight, has failed, or the caller is not a
 * school_admin, this returns null — no entry. A rail item that flickered in on every load and out
 * again a moment later would be worse than either steady state, and an item appearing because a
 * request FAILED would be the fake-empty pattern wearing navigation clothes: it would assert "your
 * setup is unfinished" from data that never arrived.
 */
export function useGettingStartedNavItem() {
  const { user } = useAuth();
  const enabled = isSchoolAdminUser(user);
  const { data, isSuccess } = useQuery({
    // SPEED wave 1, fix 9: the SHARED key, not a "rail" variant of it. This hook had its own
    // segment, which gave TanStack two caches for one endpoint and cost every admin page a second
    // request. Measured 2 -> 1 (features/onboarding/onboardingStatusRequests.test.js).
    queryKey: ONBOARDING_STATUS_KEY,
    queryFn: () => onboardingApi.status().then((r) => r.data),
    enabled,
    retry: false,
    staleTime: ONBOARDING_STATUS_STALE_MS, // setup state moves slowly; an invalidation still refetches
  });

  if (!enabled || !isSuccess) return null;
  // `completed` is derived server-side on every read (never stored stale), so an incomplete answer
  // is a live fact rather than a cached one.
  if (data?.completed) return null;
  return { label: "adminNav.gettingStarted", to: "/onboarding" };
}

export default useGettingStartedNavItem;
